import * as THREE from 'three';
import { Node, NodeMaterial } from 'three/webgpu';
import {
  Fn, uniform, vec3, vec4,
  fract, floor, sin, pow, clamp, mix, smoothstep, max, select, float,
  positionLocal, cameraViewMatrix, cameraProjectionMatrix
} from 'three/tsl';

// Procedural gradient sky + soft sun + cheap 2-octave noise clouds.
// Ported from ../shaders/sky.{vert,frag} to TSL.
//
// Vertex trick: transform vertex by the view matrix with translation stripped
// (vec4(localPos, 0) kills the translation column), then project and force
// clip-space z = w so depth = 1 after divide — sky renders behind everything.
export class SkyMaterial extends NodeMaterial {
  readonly uHorizon       = uniform(new THREE.Vector3(1.0, 0.722, 0.545));   // #ffb88b
  readonly uZenith        = uniform(new THREE.Vector3(0.227, 0.310, 0.541)); // #3a4f8a
  readonly uGround        = uniform(new THREE.Vector3(0.133, 0.094, 0.188)); // #221830
  readonly uSunColor      = uniform(new THREE.Vector3(1.0, 0.961, 0.847));   // #fff5d8
  readonly uSunDir        = uniform(new THREE.Vector3(0.4, 0.7, 0.6).normalize());
  readonly uSunSize       = uniform(0.0009);
  readonly uTime          = uniform(0);
  readonly uCloudStrength = uniform(0.55);

  constructor() {
    super();
    this.side = THREE.BackSide;
    this.depthWrite = false;
    this.depthFunc = THREE.LessEqualDepth;

    this.vertexNode = Fn(() => {
      // View matrix with translation zeroed (w=0 on the local position).
      const viewPos = cameraViewMatrix.mul(vec4(positionLocal, 0));
      const clip = cameraProjectionMatrix.mul(vec4(viewPos.xyz, 1));
      return vec4(clip.xy, clip.w, clip.w);
    })();

    const hash3 = Fn((args: [Node<'vec3'>]) => {
      const [p] = args;
      const q = vec3(
        p.dot(vec3(127.1, 311.7, 74.7)),
        p.dot(vec3(269.5, 183.3, 246.1)),
        p.dot(vec3(113.5, 271.9, 124.6))
      );
      return fract(sin(q).mul(43758.5453));
    });

    const noise = Fn((args: [Node<'vec3'>]) => {
      const [p] = args;
      const i = floor(p).toVar();
      const f = fract(p).toVar();
      const w = f.mul(f).mul(float(3).sub(f.mul(2)));

      const grad = (offX: number, offY: number, offZ: number) => {
        const off = vec3(offX, offY, offZ);
        return hash3(i.add(off)).sub(0.5).dot(f.sub(off));
      };
      const n000 = grad(0, 0, 0), n100 = grad(1, 0, 0);
      const n010 = grad(0, 1, 0), n110 = grad(1, 1, 0);
      const n001 = grad(0, 0, 1), n101 = grad(1, 0, 1);
      const n011 = grad(0, 1, 1), n111 = grad(1, 1, 1);

      const nx00 = mix(n000, n100, w.x);
      const nx10 = mix(n010, n110, w.x);
      const nx01 = mix(n001, n101, w.x);
      const nx11 = mix(n011, n111, w.x);
      const nxy0 = mix(nx00, nx10, w.y);
      const nxy1 = mix(nx01, nx11, w.y);
      return mix(nxy0, nxy1, w.z).add(0.5);
    });

    this.colorNode = Fn(() => {
      const d = positionLocal.normalize().toVar();
      const h = clamp(d.y, -1, 1);

      // Above/below horizon split — lerp both results (no divergent branching).
      const tUp = pow(float(1).sub(h), 2.2);
      const skyUp = mix(this.uZenith, this.uHorizon, tUp);
      const skyDn = mix(this.uHorizon, this.uGround, clamp(h.negate().mul(1.6), 0, 1));
      const col0 = select(h.greaterThanEqual(0), skyUp, skyDn).toVar();

      // Sun + halo.
      const sunDot = max(d.dot(this.uSunDir.normalize()), 0);
      const sun = pow(sunDot, float(1).div(max(this.uSunSize, 0.0001)));
      const halo = pow(sunDot, 6).mul(0.25);
      const withSun = col0.add(this.uSunColor.mul(sun).mul(1.3)).add(this.uSunColor.mul(halo));

      // Clouds only above horizon.
      const cp = d.mul(6).add(vec3(this.uTime.mul(0.02), 0, this.uTime.mul(0.01)));
      const n = noise(cp).mul(0.65).add(noise(cp.mul(2.1)).mul(0.35));
      const cloudMask = smoothstep(0.55, 0.85, n)
        .mul(this.uCloudStrength)
        .mul(smoothstep(0, 0.18, h));
      const withClouds = mix(withSun, vec3(0.95, 0.96, 1.0), cloudMask);

      return vec4(withClouds, 1);
    })();
  }

  setTime(t: number): void { this.uTime.value = t; }
}
